<template>
  <div
    class="main-navigation-item"
    :class="classes"
    @mouseover="mouseOver"
    @mouseleave="mouseLeave"
    @focusout="handleFocusOut"
  >
    <LinkButton
      ref="linkButtonElement"
      :class="linkClass"
      :data-tealium-rel="trackingKey"
      :rel="linkRel"
      :tag="linkTag"
      :target="linkTarget"
      :title="label"
      :to="to"
      exact
      class="main-navigation-item__link"
      no-styling
    >
      {{ label }}
    </LinkButton>
    <button
      v-if="expandable"
      class="main-navigation-item__icon-button btn btn--no-style"
      :aria-label="`${label}, Menu öffnen`"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="toggle"
    >
      <Arrow
        direction="right"
        class="main-navigation-item__icon main-navigation-item__icon--right"
        tag="span"
      />
    </button>
    <div
      v-if="expandable"
      class="main-navigation-item__icon-button main-navigation-item__icon-button--desktop"
    >
      <Arrow
        :direction="menuOpened ? 'top' : 'bottom'"
        class="main-navigation-item__icon main-navigation-item__icon--right"
        tag="button"
        :aria-label="`${label}, Menu ${menuOpened ? 'schließen' : 'öffnen'}`"
        :aria-expanded="menuOpened ? 'true' : 'false'"
        aria-haspopup="dialog"
        :aria-controls="`navigation-subnav-${uid}`"
        @click.prevent.stop=""
        @change="desktopToggle"
      />
    </div>

    <div
      v-if="expandable"
      :id="`navigation-subnav-${uid}`"
      class="main-navigation-item__subnav"
    >
      <LinkButton
        class="main-navigation-item main-navigation-item__link main-navigation-item__link--big main-navigation-item__subnav-back"
        no-styling
        tag="button"
        @click="toggle"
      >
        <Arrow
          direction="left"
          class="main-navigation-item__icon"
          tag="span"
        />
        Zurück
      </LinkButton>
      <div class="main-navigation-item__subnav-container">
        <div
          :class="modifierClass"
          class="main-navigation-item__subnav-wrapper"
        >
          <slot :menu-opened="menuOpened" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNuxtApp, useRoute, useRouter } from '#imports';
import { ref, computed, watch, onMounted, onBeforeUnmount, withDefaults } from 'vue';
import uniqueId from 'lodash-es/uniqueId';

import { Arrow, LinkButton, slugify } from '@i22/td-longtail-vue-components';
import { isAtLeastMediumDesktopScreen } from '~/composables/use-screen-size';

const EXTERNAL_URL_PREFIXES = ['http://', 'https://', '//'];

const $route = useRoute();
const $router = useRouter();
const nuxtApp = useNuxtApp();

export type MainNavigationItemProps = {
  singleLevelNavigation?: boolean;
  label: string;
  to?: string;
  exact?: boolean;
  external?: boolean;
  active?: boolean;
  expandable?: boolean;
  level?: number;
};

const props = withDefaults(defineProps<MainNavigationItemProps>(), {
  to: '',
  level: 1,
});

const uid = ref(uniqueId());

const menuOpened = ref(false); // desktop
const expanded = ref(false); // mobile

const emits = defineEmits<{
  (e: 'subnav-open', src: number): void;
}>();

const classes = computed(() => {
  const entries: string[] = [];
  if (expanded.value) {
    entries.push('main-navigation-item--expanded');
  }
  if (props.expandable) {
    entries.push('main-navigation-item--expandable');
  }
  if (menuOpened.value) {
    entries.push('main-navigation-item--hovered');
  }
  entries.push(`main-navigation-item--${slugify(props.label)}`);
  return entries;
});

const linkClass = computed(() => {
  const entries = [];
  if (props.level === 1) {
    entries.push('main-navigation-item__link--big');
  }
  if (props.active) {
    entries.push('main-navigation-item__link--active');
  }
  return entries;
});

const trackingKey = computed(() => `navi-top.text.${slugify(props.label)}`);

const isExternal = computed(() => {
  if (props.external !== undefined) return props.external;
  if (!props.to) return false;
  return EXTERNAL_URL_PREFIXES.find((prefix) => props.to.startsWith(prefix));
});

const targetRoute = computed(() => {
  if (isExternal.value || !props.to) return undefined;
  const result = $router.resolve(props.to);
  if (!result) return undefined;
  return result.route;
});

const isActive = computed(() => {
  if (props.active !== undefined) return props.active;
  if (!targetRoute.value) return false;
  if (props.exact) {
    return $route.fullPath === targetRoute.value.fullPath;
  }
  return $route.path.startsWith(targetRoute.value.path);
});

const modifierClass = computed(() => {
  const entries = [];
  if (props.singleLevelNavigation) {
    entries.push('main-navigation-item__subnav-wrapper--single-level');
  }
  return entries;
});

const linkTarget = computed(() => (props.external ? '_blank' : '_self'));

const linkTag = computed(() => {
  // empty "to" will add "nuxt-link-active"-class
  if (!props.to) return 'a';
  return props.external ? 'a' : 'nuxt-link';
});

const linkRel = computed(() => (props.external ? 'noopener' : ''));

watch($route, () => {
  expanded.value = false; // close submenu on route change
  menuOpened.value = false;
});

watch(expanded, () => {
  emits('subnav-open', expanded.value ? props.level : props.level - 1);
});

const toggle = (e) => {
  if (!props.expandable) return;
  e.preventDefault();
  expanded.value = !expanded.value;
};

const desktopToggle = () => {
  menuOpened.value = !menuOpened.value;
};

const click = () => {
  // call to close all open menus
  nuxtApp.hooks.callHook('close-menu');

  document.querySelector('body')?.classList?.remove('mobile-nav-open');
};

const mouseOver = (e) => {
  const isTouch = e?.sourceCapabilities?.firesTouchEvents || false;
  // do not open flyout on touch-device.
  if (isTouch) return;
  menuOpened.value = true;
};

const mouseLeave = () => {
  menuOpened.value = false;
};

const handleFocusOut = (event: FocusEvent) => {
  if (menuOpened.value && event.relatedTarget) {
    const navItem = event.currentTarget as HTMLElement;
    const newTarget = event.relatedTarget as HTMLElement;

    if (!navItem.contains(newTarget)) {
      menuOpened.value = false;
    }
  }
};

const linkButtonElement = ref<typeof LinkButton | null>(null);
onMounted(() => {
  // the event from `nuxt-link` cannot be stopped by using "@click.prevent". So we have to do it so.

  if (linkButtonElement.value?.$el) {
    linkButtonElement.value.$el.addEventListener(
      'click',
      (e: PointerEvent) => {
        const isTouch = e.pointerType === 'touch';
        if (isTouch && props.expandable && isAtLeastMediumDesktopScreen.value) {
          // first touch: open flyout.
          if (!menuOpened.value) {
            menuOpened.value = true;
            e.preventDefault();
            return;
          }
        }
        // close mobile menu immediately to show loading spinner.
        click();
        menuOpened.value = false;
      },
      true
    );
  }

  nuxtApp.hook('close-menu', () => {
    menuOpened.value = false;
  });

  const handleEscapeKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && menuOpened.value) {
      menuOpened.value = false;
    }
  };

  document.addEventListener('keydown', handleEscapeKey);

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleEscapeKey);
  });
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.main-navigation-item {
  padding-inline: $grid-base * 3;

  @include mq(false, $mq-lg - 1) {
    border-bottom: 1px solid var(--color-stroke-subtle);
  }
  @include mq($mq-lg) {
    padding-inline: $grid-base * 1.5;
    display: flex;
  }

  &__link {
    white-space: normal;
    text-transform: none;
    cursor: pointer;
    text-decoration: none;
    margin: 0;
    background: none;
    color: var(--color-font-subtle);
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include mq(false, $mq-lg - 1) {
      width: 100%;
      min-height: 60px;
    }

    @include mq($mq-lg) {
      display: block;
    }

    &:hover,
    &.nuxt-link-exact-active,
    &--active {
      color: var(--color-primary);
      text-decoration: none;
    }

    &:not(&--big) {
      @include font-size(20px, 20px);
      padding-block: 6px;

      @include mq($mq-lg) {
        @include font-size(16px, 22px);
        padding: 4px 0;
        margin-bottom: 8px;
      }
    }

    &--big {
      font-weight: 800;

      @include mq(false, $mq-lg - 1) {
        @include font-size(24px, 24px);
        min-height: 80px;
      }

      @include mq($mq-lg) {
        display: flex;
        height: 70px;
        position: relative;
      }

      &.nuxt-link-active {
        color: var(--color-primary);
      }
    }
  }

  &--expandable {
    @include mq(false, $mq-lg - 1) {
      display: flex;

      > .main-navigation-item__link {
        width: auto;
        flex: 0 1 auto;
      }

      > .main-navigation-item__icon-button {
        flex: 1 1 0;
      }
    }

    @include mq($mq-lg) {
      padding-inline: $grid-base;
    }
  }

  &--expanded {
    @include mq(false, $mq-lg - 1) {
      // This ensures, the Menu is closed, when a link is clicked
      > .main-navigation-item__subnav {
        display: block;
      }
    }
  }

  &:focus,
  &:hover {
    @include mq($mq-lg) {
      &.main-navigation-item__link--big,
      > .main-navigation-item__link--big {
        color: var(--color-primary);
        fill: var(--color-primary);

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
          background-color: var(--color-primary);
        }
      }

      &.main-navigation-item__link--big {
        &::after {
          left: $grid-base;
          right: $grid-base;

          @include mq($mq-lg) {
            left: $grid-base * 1.5;
            right: $grid-base * 1.5;
          }
        }
      }
    }
  }

  &--hovered {
    @include mq($mq-lg) {
      > .main-navigation-item__subnav {
        visibility: visible;
      }
    }
  }

  &__subnav {
    position: absolute;
    top: 0;
    width: 100%;

    @include mq(false, $mq-lg - 1) {
      left: 100%;
      display: none;
    }

    @include mq($mq-lg) {
      visibility: hidden;
      @include font-size(16px, 22px);
      top: 100%;
      left: 0;
      height: calc(100vh - 96px);
      pointer-events: none;
      background: rgb(from var(--color-stroke-subtle) r g b / 0.6);

      // This overlay prevents to hover the whitespace from other nav items.
      &:before {
        $navItemSpace: 26px; // navItemHeight - Line-Height (real height)
        content: '';
        left: 0;
        right: 0;
        top: $navItemSpace * -1;
        height: $navItemSpace;
        z-index: 2;
        position: absolute;
        pointer-events: all; // catch mouse
      }
    }
  }

  &__subnav-back {
    justify-content: flex-start;
    padding-left: $grid-base * 2;

    @include mq($mq-lg) {
      display: none;
    }
  }

  &__subnav-container {
    @include mq($mq-lg) {
      background: var(--color-bg);
      pointer-events: all;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      max-height: 100%;
      box-shadow: inset 0 2px 9px 0 rgb(from var(--color-stroke) r g b / 0.15);
    }
  }

  &__subnav-wrapper {
    @include mq(false, $mq-lg - 1) {
      margin-bottom: $grid-base * 9;
    }

    @include mq($mq-lg) {
      width: 100%;
      position: relative;
      max-width: $mq-lpg-lg-width + $grid-base * 4;
      margin: 0 auto;
      padding: $grid-base * 2;
      min-height: 250px;
      grid-gap: $grid-base * 2 $grid-base * 8;

      &:not(&--single-level) {
        display: flex;
        justify-content: space-between;
        column-gap: 2 * $grid-base;
      }
    }
  }

  &__icon {
    --arrow-position: relative;
    color: currentColor;
    font-size: 1.5rem;
    margin-right: $grid-base * 0.5;
    @include mq($mq-lg) {
      margin-left: $grid-base * 0.5;
      margin-right: 0;
    }

    &--left {
      margin-left: $grid-base * -1;
    }

    &-button {
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: flex-end;

      @include mq($mq-lg) {
        display: none;
      }

      &--desktop {
        display: none;

        @include mq($mq-lg) {
          display: flex;
        }

        :deep(.arrow.link-button) {
          width: 0.3em;
          height: 0.3em;
          position: static;
          opacity: 0;

          &:focus {
            opacity: 1;
          }
        }
      }
    }
  }
}

body.nav-scrolled {
  .main-navigation-item__subnav {
    @include mq($mq-lg) {
      height: calc(100vh - 70px);
    }
  }
}
</style>
